import styled from "@emotion/styled";
import { Button, Section, Fonts } from "@snapchat/snap-design-system";
import { ExternalLinkIcon } from "@snapchat/snap-design-system-icons";
import { type FC, useMemo, useState } from "react";
import { Visible } from "../../../common/components";
import { Flexbox } from "../../../core/components";
import { MyLensesPlatformType } from "../../../../gql-generated/types";
import { appInstructions } from "../appInstructions";
import { OSOptions } from "../types";
import { CopyInput } from "./CopyInput";
import { OsDropdown } from "./OsDropdown";

type Instructions = typeof appInstructions;

type AppInstallOverviewProps = {
    lensGroupId: string;
    stagingApiToken?: string;
    productionApiToken?: string;
    platformType?: MyLensesPlatformType | null;
};

export const AppInstallOverview: FC<AppInstallOverviewProps> = ({
    lensGroupId,
    stagingApiToken,
    productionApiToken,
    platformType,
}) => {
    const [selectedOs, setSelectedOs] = useState(OSOptions.IOS);
    const isWeb = platformType === MyLensesPlatformType.PlatformTypeWeb;
    const installInstructions = useMemo(() => {
        const platform = isWeb ? "web" : (selectedOs.toLocaleLowerCase() as keyof Instructions);
        return appInstructions[platform];
    }, [isWeb, selectedOs]);

    const mobileText =
        "Select your operating system and follow instructions to integrate Camera Kit into your application";
    const webText = "Follow instructions to integrate Camera Kit into your application";

    return (
        <Section
            title="IDs and Tokens"
            subtitle={isWeb ? webText : mobileText}
            rightHeaderContent={
                <Visible when={!isWeb}>
                    <OsDropdown selectdOs={selectedOs} onSelectOs={setSelectedOs} />
                </Visible>
            }
        >
            <Container>
                <Row>
                    <CopyInput label="Demo Lens Group ID" value={lensGroupId} />
                    <InstructionsCopy
                        data-testid="app-lens-group-id"
                        dangerouslySetInnerHTML={{ __html: installInstructions.groupId }}
                    />
                </Row>
                <Row>
                    <CopyInput
                        label="Staging API Token"
                        value={stagingApiToken || "Missing token"}
                        statusType={!stagingApiToken?.length ? "warning" : undefined}
                    />
                    <InstructionsCopy
                        data-testid="app-staging-api"
                        dangerouslySetInnerHTML={{ __html: installInstructions.stagingToken }}
                    />
                </Row>
                <Row>
                    <CopyInput
                        label="Production API Token"
                        value={productionApiToken || "Missing token"}
                        statusType={!productionApiToken?.length ? "warning" : undefined}
                    />
                    <InstructionsCopy
                        data-testid="app-prod-api"
                        dangerouslySetInnerHTML={{ __html: installInstructions.productionToken }}
                    />
                </Row>
                <ButtonContainer>
                    <a href={installInstructions.sampleApp} target="_blank">
                        <Button type="primary" isElevated>
                            <ExternalLinkIcon />
                            Sample App
                        </Button>
                    </a>
                    <a href={installInstructions.configDetails} target="_blank">
                        <Button isElevated>
                            <ExternalLinkIcon />
                            Config Instructions
                        </Button>
                    </a>
                </ButtonContainer>
            </Container>
        </Section>
    );
};

const Container = styled(Flexbox)`
    flex-direction: column;
    gap: 16px;
    padding-bottom: 8px;
`;

const Row = styled(Flexbox)`
    gap: 16px;
    align-items: flex-end;
`;

const InstructionsCopy = styled.span`
    ${Fonts.Paragraph.P200};
    flex-basis: 50%;
    flex-grow: 1;

    code {
        padding: 0 4px;
        background-color: #f0f0f0;
        border: 1px solid #dedfe0;
        border-radius: 4px;
    }
`;

const ButtonContainer = styled.div`
    display: flex;
    gap: 15px;
    justify-content: center;
    margin-top: 30px;
`;
